// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Pressable, ScrollView, StyleSheet } from 'react-native';
import Markdown from 'react-native-markdown-display';

import { Text, View } from '@/components/Themed';
import { getRuleSetDisplayDescription, getRuleSetDisplayName } from '@/features/game-definitions/i18n';
import type { GameRuleSet } from '@/features/game-definitions/types';
import { sortRuleSets } from '@/features/game-selection/ruleSets';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

type GameRulesPickerProps = {
  gameId?: string | null;
  gameName: string;
  ruleSets: GameRuleSet[];
  selectedRuleSetId: string | null;
  /** The tour's default, badged when it is not the one selected. */
  tourRuleSetId?: string | null;
  /** Omit to render read-only. */
  onSelect?: (ruleSetId: string) => void;
  scope?: 'round' | 'tour';
  onBack: () => void;
  /** Dismisses the whole modal. Back returns to the info view; this leaves entirely. */
  onClose?: () => void;
};

/**
 * The rules view of the game info modal.
 *
 * Deliberately not a `<Modal>` of its own. No screen in this app stacks two React Native modals:
 * on iOS a second simultaneously-visible sibling modal silently never presents, and nesting one
 * inside the other's tree double-stacks the overlay and animates twice. RoundGameInfoModal keeps
 * its single modal and swaps its body between the info view and this one.
 */
export default function GameRulesPicker({
  gameId,
  gameName,
  ruleSets,
  selectedRuleSetId,
  tourRuleSetId,
  onSelect,
  scope = 'round',
  onBack,
  onClose,
}: GameRulesPickerProps) {
  const { t } = useI18n();
  const sorted = sortRuleSets(ruleSets);
  const readOnly = typeof onSelect !== 'function';

  return (
    <View style={styles.container} testID="game-rules-picker">
      <View style={styles.header}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('selectGames.rulesBack')}
          onPress={onBack}
          style={styles.backButton}
          testID="game-rules-back"
        >
          <Text style={styles.backButtonText}>←</Text>
        </Pressable>
        <Text style={styles.title}>{t('selectGames.rulesTitle', { name: gameName })}</Text>
        {onClose ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('selectGames.rulesClose')}
            onPress={onClose}
            style={styles.closeButton}
            testID="game-rules-close"
          >
            <Text style={styles.closeButtonText}>✕</Text>
          </Pressable>
        ) : null}
      </View>
      <Text style={styles.subtitle}>
        {scope === 'tour' ? t('selectGames.rulesSubtitleTour') : t('selectGames.rulesSubtitleRound')}
      </Text>
      {readOnly ? <Text style={styles.readOnlyHint}>{t('selectGames.rulesReadOnlyHint')}</Text> : null}

      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        {sorted.length === 0 ? (
          <Text style={styles.empty}>{t('selectGames.rulesEmpty')}</Text>
        ) : (
          <View style={styles.group} accessibilityRole="radiogroup">
            {sorted.map((ruleSet) => {
              const isSelected = ruleSet.id === selectedRuleSetId;
              const name = getRuleSetDisplayName(gameId, ruleSet, ruleSet.id);
              const description = getRuleSetDisplayDescription(gameId, ruleSet);
              return (
                <Pressable
                  key={ruleSet.id}
                  testID={`game-rule-option-${ruleSet.id}`}
                  accessibilityRole="radio"
                  // react-native-web 0.21 drops accessibilityState entirely, so the aria props are
                  // what actually reach the DOM. Without them every assertion on the selected
                  // option — RNTL and Playwright alike — passes without checking anything.
                  aria-checked={isSelected}
                  aria-disabled={readOnly}
                  accessibilityState={{ checked: isSelected, disabled: readOnly }}
                  accessibilityLabel={t('selectGames.rulesA11ySelect', { name })}
                  disabled={readOnly}
                  onPress={() => onSelect?.(ruleSet.id)}
                  style={[styles.row, isSelected && styles.rowSelected]}
                >
                  <View style={styles.rowHeader}>
                    <Text style={styles.radio}>{isSelected ? '◉' : '○'}</Text>
                    <Text style={styles.rowTitle}>{name}</Text>
                  </View>
                  <View style={styles.badges}>
                    {ruleSet.isDefault === true ? (
                      <Text style={styles.badge}>{t('selectGames.rulesDefaultBadge')}</Text>
                    ) : null}
                    {tourRuleSetId === ruleSet.id && !isSelected ? (
                      <Text style={styles.badge}>{t('selectGames.rulesTourDefaultBadge')}</Text>
                    ) : null}
                  </View>
                  {description ? (
                    <Markdown style={descriptionMarkdownStyles}>{description}</Markdown>
                  ) : (
                    <Text style={styles.rowDescription}>{t('selectGames.rulesNoDescription')}</Text>
                  )}
                </Pressable>
              );
            })}
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    backgroundColor: 'transparent',
    gap: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    gap: 8,
  },
  backButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  backButtonText: {
    fontSize: 18,
    color: appColors.text.secondary,
  },
  closeButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  closeButtonText: {
    fontSize: 16,
    color: appColors.text.secondary,
  },
  title: {
    flex: 1,
    fontSize: 18,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  subtitle: {
    fontSize: 13,
    color: appColors.text.secondary,
  },
  readOnlyHint: {
    fontSize: 12,
    color: appColors.text.muted,
  },
  scroll: {
    marginTop: 4,
  },
  scrollContent: {
    gap: 10,
    paddingBottom: 4,
  },
  group: {
    backgroundColor: 'transparent',
    gap: 10,
  },
  empty: {
    fontSize: 14,
    color: appColors.text.secondary,
    textAlign: 'center',
    paddingVertical: 16,
  },
  row: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.alt,
    padding: 12,
    gap: 6,
  },
  rowSelected: {
    borderColor: appColors.accent.primary,
    backgroundColor: appColors.surface.elevated,
  },
  rowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    gap: 8,
  },
  radio: {
    fontSize: 15,
    color: appColors.accent.primary,
  },
  rowTitle: {
    flex: 1,
    fontSize: 15,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  badges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: 'transparent',
    gap: 6,
  },
  badge: {
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    fontWeight: '700',
    color: appColors.text.muted,
  },
  rowDescription: {
    fontSize: 13,
    lineHeight: 18,
    color: appColors.text.secondary,
  },
});

/** Plain object, not StyleSheet.create — react-native-markdown-display merges these into its
 * own default rule styles per node type, and expects raw style objects to merge into. */
const descriptionMarkdownStyles = {
  body: { margin: 0, padding: 0 },
  paragraph: { marginTop: 0, marginBottom: 0 },
  text: { fontSize: 13, lineHeight: 18, color: appColors.text.secondary },
  strong: { fontWeight: '700' as const },
  bullet_list: { marginTop: 2, marginBottom: 0 },
  ordered_list: { marginTop: 2, marginBottom: 0 },
  list_item: { marginTop: 0, marginBottom: 0 },
  link: { color: appColors.accent.primary, textDecorationLine: 'underline' as const },
};
